/**
 * Per-waypoint editor for the demo Dock Mission planner.
 *
 * Each waypoint renders as a vertical card with stacked fields, so it reads
 * cleanly inside the right-side ticket without needing horizontal scroll.
 * Reorder via up/down buttons in the card header, delete via the × button.
 */

import { useEffect, useRef } from 'react';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type {
  DemoMission,
  DemoWaypoint,
  DemoWaypointAction,
  DemoPhotoPayload,
} from './types';

interface Props {
  mission: DemoMission;
}

// Recording is no longer a waypoint action — it lives on the leg leaving
// the waypoint (see MissionLegEditor). Only discrete per-waypoint actions
// remain: 'none' / 'photo' / 'hover'.
const ACTIONS: DemoWaypointAction[] = ['none', 'photo', 'hover'];
const PAYLOADS: DemoPhotoPayload[] = ['wide', 'zoom', 'ir'];

function fmtCoord(value: number): string {
  return value.toFixed(5);
}

export default function WaypointEditorDemo({ mission }: Props) {
  const updateWaypoint = useDockDemoStore((s) => s.updateWaypoint);
  const deleteWaypoint = useDockDemoStore((s) => s.deleteWaypoint);
  const reorderWaypoint = useDockDemoStore((s) => s.reorderWaypoint);
  const hoveredWaypointId = useDockDemoStore((s) => s.hoveredWaypointId);
  const selectedWaypointId = useDockDemoStore((s) => s.selectedWaypointId);
  const setHoveredWaypointId = useDockDemoStore((s) => s.setHoveredWaypointId);

  // Per-waypoint card refs so a click on the map marker (which sets
  // selectedWaypointId in the store) can scroll the matching card into
  // view. Using a Map keyed by waypoint id keeps the lookup O(1) and
  // tolerates reorder/delete without index drift.
  const cardRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Scroll the selected card into view whenever the selection changes.
  // `block: 'nearest'` keeps the scroll minimal — if the card is already
  // visible we don't snap the container, only nudge as needed.
  useEffect(() => {
    if (!selectedWaypointId) return;
    const el = cardRefs.current.get(selectedWaypointId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedWaypointId]);

  if (mission.waypoints.length === 0) {
    return (
      <div className="dock-demo-waypoint-empty">
        <p>No waypoints yet.</p>
        <p className="dock-demo-hint">
          Click <strong>Place waypoints</strong> above, then click on the map to drop points.
        </p>
      </div>
    );
  }

  const handlePatch = (wpId: string, patch: Partial<DemoWaypoint>) => {
    updateWaypoint(mission.id, wpId, patch);
  };

  return (
    <div className="dock-demo-wp-cards">
      {mission.waypoints.map((wp, idx) => (
        <div
          className={`dock-demo-wp-card${
            hoveredWaypointId === wp.id ? ' is-hovered' : ''
          }${selectedWaypointId === wp.id ? ' is-selected' : ''}`}
          key={wp.id}
          ref={(el) => {
            if (el) cardRefs.current.set(wp.id, el);
            else cardRefs.current.delete(wp.id);
          }}
          onMouseEnter={() => setHoveredWaypointId(wp.id)}
          onMouseLeave={() => setHoveredWaypointId(null)}
        >
          <div className="dock-demo-wp-card__head">
            <span className="dock-demo-wp-card__idx">#{idx + 1}</span>
            <span className="dock-demo-wp-card__coord" title={`${fmtCoord(wp.lng)}, ${fmtCoord(wp.lat)}`}>
              {fmtCoord(wp.lng)}, {fmtCoord(wp.lat)}
            </span>
            <div className="dock-demo-wp-card__ctrl">
              <button
                type="button"
                className="dock-demo-wp-btn"
                disabled={idx === 0}
                onClick={() => reorderWaypoint(mission.id, idx, idx - 1)}
                title="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                className="dock-demo-wp-btn"
                disabled={idx === mission.waypoints.length - 1}
                onClick={() => reorderWaypoint(mission.id, idx, idx + 1)}
                title="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                className="dock-demo-wp-btn dock-demo-wp-btn--del"
                onClick={() => deleteWaypoint(mission.id, wp.id)}
                title="Delete waypoint"
              >
                ×
              </button>
            </div>
          </div>

          <div className="dock-demo-wp-card__grid">
            <label className="dock-demo-wp-field">
              <span>Alt (m)</span>
              <input
                type="number"
                min={0}
                max={1500}
                value={wp.alt}
                onChange={(e) => handlePatch(wp.id, { alt: Number(e.target.value) || 0 })}
              />
            </label>

            <label className="dock-demo-wp-field">
              <span>Heading</span>
              <input
                type="number"
                min={0}
                max={360}
                placeholder="auto"
                value={wp.heading ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handlePatch(wp.id, { heading: v === '' ? undefined : Number(v) });
                }}
              />
            </label>

            <label className="dock-demo-wp-field">
              <span>Pitch (°)</span>
              <input
                type="number"
                min={-90}
                max={30}
                value={wp.gimbalPitch ?? -45}
                onChange={(e) => handlePatch(wp.id, { gimbalPitch: Number(e.target.value) || 0 })}
              />
            </label>

            <label className="dock-demo-wp-field" title="Absolute world-frame: 0=N, 90=E, 180=S, 270=W. Empty = follow aircraft heading.">
              <span>Gimbal yaw</span>
              <input
                type="number"
                min={0}
                max={360}
                placeholder="auto"
                value={wp.gimbalYaw ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handlePatch(wp.id, { gimbalYaw: v === '' ? undefined : Number(v) });
                }}
              />
            </label>

            <label className="dock-demo-wp-field">
              <span>Speed (m/s)</span>
              <input
                type="number"
                min={1}
                max={15}
                placeholder="global"
                value={wp.speedMps ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handlePatch(wp.id, { speedMps: v === '' ? undefined : Number(v) });
                }}
              />
            </label>

            <label className="dock-demo-wp-field">
              <span>Action</span>
              <select
                value={wp.action}
                onChange={(e) => handlePatch(wp.id, { action: e.target.value as DemoWaypointAction })}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </label>

            {wp.action === 'photo' && (
              // photoPayload is multi-select for DJI "AB capture" — selecting
              // both 'wide' and 'ir' triggers a paired RGB+thermal shutter in
              // a single takePhoto, producing matched-timestamp file pairs.
              <fieldset className="dock-demo-wp-field dock-demo-wp-lensfield">
                <legend>Photo lens(es)</legend>
                {PAYLOADS.map((p) => {
                  const selected = wp.photoPayload?.includes(p) ?? false;
                  return (
                    <label key={p} className="dock-demo-wp-lenschip">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          const cur = wp.photoPayload ?? [];
                          const next = e.target.checked
                            ? Array.from(new Set([...cur, p]))
                            : cur.filter((x) => x !== p);
                          handlePatch(wp.id, {
                            photoPayload: next.length > 0 ? (next as DemoPhotoPayload[]) : undefined,
                          });
                        }}
                      />
                      {p}
                    </label>
                  );
                })}
              </fieldset>
            )}

            {wp.action === 'hover' && (
              <label className="dock-demo-wp-field">
                <span>Hover (s)</span>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={wp.hoverSec ?? 2}
                  onChange={(e) => handlePatch(wp.id, { hoverSec: Number(e.target.value) || 0 })}
                />
              </label>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
